import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaClock, FaTimesCircle } from "react-icons/fa";
import { Menu } from "lucide-react";
import mainlogo from "../assets/main-logo.png";
import "./Header.css";
import TempleStatusPopup from "./TempleStatusPopup";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [templeStatus, setTempleStatus] = useState({ status: "", upcoming: {} });
  const popupRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const toggleDropdown = (menu) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const togglePopup = () => setIsOpen((prev) => !prev);
  // Move timings array OUTSIDE the component
  const timings = [
    { time: "04:10", label: "Samadhi Aarti" },
    { time: "04:30", label: "Mangal Aarti" },
    { time: "05:30", label: "Japa Class" },
    { time: "07:25", label: "Narshima Aarti" },
    { time: "07:30", label: "Darshan Aarti" },
    { time: "08:00", label: "Bhagvatam Class" },
    { time: "12:15", label: "Raj Bhoga" },
    { time: "16:00", label: "Dhupa Aarti" },
    { time: "18:45", label: "Tulsi Aarti" },
    { time: "21:00", label: "Shayana Aarti" },
  ];

  const timeToMinutes = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const getTempleStatus = () => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const closedPeriods = [
      { start: timeToMinutes("13:00"), end: timeToMinutes("16:00") },
      { start: timeToMinutes("21:15"), end: 24 * 60 },
      { start: 0, end: timeToMinutes("04:10") },
    ];

    const isClosed = closedPeriods.some(({ start, end }) =>
      currentMinutes >= start && currentMinutes < end
    );

    const upcoming =
      timings.find(({ time }) => timeToMinutes(time) > currentMinutes) || timings[0];

    return {
      status: isClosed ? "Closed" : "Open",
      upcoming,
    };
  };


  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setTempleStatus(getTempleStatus());

    const interval = setInterval(() => {
      setTempleStatus(getTempleStatus());
    }, 60000);

    return () => clearInterval(interval);
  }, []); // No need to pass getTempleStatus since it's now stable


  return (
    <header className="navbar">
      <div className="top-bar">
        <Link to="/" className="logo">
          <img className="logo" src={mainlogo} alt="ISKCON Logo" />
        </Link>
        <button
          className="hamburger"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <Menu size={28} />
        </button>

        {mobileMenuOpen && <div className="mobile-overlay" onClick={closeMobileMenu}></div>}

        <nav className={`nav ${mobileMenuOpen ? "show-mobile" : ""}`}>
          <Link
            to="/"
            onClick={closeMobileMenu}
            className={location.pathname === "/" ? "active-link" : ""}
          >
            Home
          </Link>

          <div
            className={`nav-item dropdown color-dark ${activeDropdown === "about" || location.pathname.startsWith("/about") ? "active" : ""
              }`}
          >
            <span className="nav-link" onClick={() => toggleDropdown("about")}>
              About
            </span>
            <div className={`dropdown-menu ${activeDropdown === "about" ? "show" : ""}`}>
              <Link to="/about/iskconujjain" onClick={closeMobileMenu}>ISKCON Ujjain</Link>
              <Link to="/about/SrilaPrabhupada" onClick={closeMobileMenu}>Srila Prabhupada</Link>
              <Link to="/about/BcsSwami" onClick={closeMobileMenu}>HH Bhakti Charu Swami Maharaja</Link>
            </div>
          </div>

          <Link
            to="/calendar"
            onClick={closeMobileMenu}
            className={location.pathname === "/calendar" ? "active-link" : ""}
          >
            Vaishnava Calendar
          </Link>
          <Link
            to="/contact"
            onClick={closeMobileMenu}
            className={location.pathname === "/contact" ? "active-link" : ""}
          >
            Contact
          </Link>
          <div
            className={`nav-item dropdown color-dark ${activeDropdown === "media" ||
              location.pathname === "/gallery" ||
              location.pathname === "/DailyDarshan"
              ? "active"
              : ""
              }`}
          >
            <span
              className="nav-link"
              onClick={() => toggleDropdown("media")}
            >
              Media
            </span>

            <div
              className={`dropdown-menu ${activeDropdown === "media" ? "show" : ""
                }`}
            >
              <Link to="/gallery" onClick={closeMobileMenu}>
                Videos
              </Link>

              <Link to="/DailyDarshan" onClick={closeMobileMenu}>
                Daily Darshan
              </Link>
            </div>
          </div>
          <div
            className={`nav-item dropdown color-dark ${activeDropdown === "services" || location.pathname.startsWith("/services")
              ? "active"
              : ""
              }`}
          >
            <span
              className="nav-link"
              onClick={() => toggleDropdown("services")}
            >Our Services
            </span>

            <div
              className={`dropdown-menu ${activeDropdown === "services" ? "show" : ""
                }`}
            >
              <Link to="/services/goshala" onClick={closeMobileMenu}>
                Goshala
              </Link>

              <a
                href="https://aihe.iskconujjain.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                AIHE
              </a>

              <a
                href="https://guesthouse.iskconujjain.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                Guest House
              </a>

              <a
                href="https://www.divinesky.net/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                ISKCON Altars
              </a>

              <a
                href="https://arogyaniketan.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                Ayurvedic Clinic
              </a>

              <Link to="/services/deities" onClick={closeMobileMenu}>
                Deities Dress
              </Link>

              <Link to="/services/govindas" onClick={closeMobileMenu}>
                Govinda's Restaurant
              </Link>
            </div>
          </div>

          <button className="schedule-btn" onClick={() => { closeMobileMenu(); togglePopup(); }}>
            Schedule
          </button>

          <a
            href="https://iskconujjaindonation.com/"
            onClick={closeMobileMenu}
            className="donate-btn cstm-link"
            target="_blank"
            rel="noopener noreferrer"
          >Donate
          </a>
        </nav>
      </div>

      <TempleStatusPopup templeStatus={templeStatus} />

      {isOpen && (
        <div className="modal-overlay">
          <div className="schedule-modal" ref={popupRef}>
            <div className="modal-header">
              <h2 className="text-center">ISKCON Ujjain Temple Timings</h2>
              <button onClick={togglePopup} className="close-btn">
                <FaTimesCircle size={24} />
              </button>
            </div>
            <p className="closed-time text-center">Closed from 13:00 – 16:00 and 21:15 – 04:10</p>
            <div className="timing-grid">
              {timings.map((item, index) => (
                <div key={index} className="timing-item">
                  <FaClock className="clock-icon" />
                  <span className="time">{item.time}</span>
                  <span className="label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
